import React, { useEffect, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import { fetchVaultAttachmentBlob } from './vault_file_api';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif']);

/** file_id → object URL or data URL (module cache across remounts) */
const thumbCache = new Map();

function extOf(finfo) {
  return String(finfo.extn || finfo.file_name?.split('.').pop() || '')
    .toLowerCase()
    .replace(/^\./, '');
}

function fallbackIcon(ext) {
  if (ext === 'pdf') return '📄';
  if (IMAGE_EXTS.has(ext)) return '🖼️';
  if (ext === 'doc' || ext === 'docx') return '📝';
  return '📎';
}

async function renderPdfFirstPage(data) {
  const loadingTask = pdfjsLib.getDocument({ data, withCredentials: false });
  const pdf = await loadingTask.promise;
  const page = await pdf.getPage(1);
  const baseViewport = page.getViewport({ scale: 1 });
  const maxSide = 160;
  const scale = Math.min(maxSide / baseViewport.width, maxSide / baseViewport.height, 1.5);
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const ctx = canvas.getContext('2d');
  await page.render({ canvasContext: ctx, viewport }).promise;
  return canvas.toDataURL('image/jpeg', 0.82);
}

async function buildThumbnail(finfo) {
  const cacheKey = String(finfo.id);
  if (thumbCache.has(cacheKey)) {
    return thumbCache.get(cacheKey);
  }

  const ext = extOf(finfo);
  // Same-origin stream — avoids R2 CORS failures in the browser.
  const blob = await fetchVaultAttachmentBlob(finfo);

  let thumb = null;
  if (IMAGE_EXTS.has(ext)) {
    thumb = URL.createObjectURL(blob);
  } else if (ext === 'pdf') {
    const buffer = await blob.arrayBuffer();
    thumb = await renderPdfFirstPage(new Uint8Array(buffer));
  }

  if (thumb) {
    thumbCache.set(cacheKey, thumb);
  }
  return thumb;
}

const AttachmentThumb = ({ finfo }) => {
  const ext = extOf(finfo);
  const [src, setSrc] = useState(() => thumbCache.get(String(finfo.id)) || null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = String(finfo.id);

    if (finfo.pending) return undefined;
    if (!/^\d+$/.test(String(finfo.id))) return undefined;
    if (thumbCache.has(cacheKey)) {
      setSrc(thumbCache.get(cacheKey));
      return undefined;
    }
    if (!IMAGE_EXTS.has(ext) && ext !== 'pdf') {
      return undefined;
    }

    setFailed(false);
    buildThumbnail(finfo)
      .then((thumb) => {
        if (!cancelled) setSrc(thumb || null);
      })
      .catch((err) => {
        console.warn('attachment thumb failed:', finfo.file_name, err);
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [finfo.id, finfo.pending, finfo.file_name, ext]);

  if (src && !failed) {
    return (
      <div className="attachment-thumb">
        <img src={src} alt={finfo.name || finfo.file_name || 'attachment'} />
      </div>
    );
  }

  return <div className="attachment-icon">{fallbackIcon(ext)}</div>;
};

export default AttachmentThumb;
